/**
 * GÖREV TAŞIMA HESABI — F-SCH-07
 *
 * `POST /program/gorevler/:id/tasi` bir görevin gün ve sırasını
 * yazıyor. Hangi güne, hangi sıraya yazılacağını hesaplamak ekranın
 * işi ve bu dosya o hesabı JSX'ten ayırıyor: sürükle-bırak yerine
 * düğme kullanıldığı için hesap küçük ama üç ayrı yerde yanlış
 * yapılabilir (hafta sınırı, boş gün, eşit sıra numarası).
 *
 * SÜRÜKLE-BIRAK YOK, BİLİNÇLİ. Sistem mobil öncelikli; sürükleme
 * telefonda tek elle kullanılamıyor ve ekran okuyucuyla erişilemiyor.
 * Düğme her ikisinde de çalışıyor.
 */

export interface TasinabilirGorev {
  id: string;
  /** `YYYY-MM-DD` — sunucudan gelen `itemDate`'in gün kısmı */
  gun: string;
  sira: number;
}

export interface TasimaSonucu {
  /** Uca gönderilecek çağrılar, sırayla. Boşsa yapılacak bir şey yok. */
  cagrilar: Array<{ gorevId: string; gun: string; sira: number }>;
  /** Yapılamıyorsa sebebi — düğme kapatılırken gösterilir. */
  engel: string | null;
}

const YAPILAMAZ = (engel: string): TasimaSonucu => ({ cagrilar: [], engel });

/**
 * Görevi komşu güne taşır.
 *
 * Hafta dışına çıkılamıyor (sunucu da reddediyor, `HAFTA_DISI`), o
 * yüzden düğme orada kapatılıyor — reddedilecek bir düğmeyi
 * göstermek kullanıcıya yapamayacağı işi denettirmek olur.
 *
 * Hedef günün SONUNA ekleniyor: bir görevin gün değiştirirken
 * başkalarının arasına girmesi, kullanıcının istemediği ikinci bir
 * değişiklik olurdu.
 */
export function gunDegistir(
  gorev: TasinabilirGorev,
  hedefGun: string,
  haftaGunleri: readonly string[],
  hedefGunGorevleri: readonly TasinabilirGorev[],
): TasimaSonucu {
  if (!haftaGunleri.includes(hedefGun)) {
    return YAPILAMAZ("Görev bu haftanın dışına taşınamaz.");
  }
  if (hedefGun === gorev.gun) return YAPILAMAZ("Görev zaten bu günde.");

  const enBuyuk = hedefGunGorevleri.reduce((a, g) => (g.sira > a ? g.sira : a), -1);
  return { cagrilar: [{ gorevId: gorev.id, gun: hedefGun, sira: enBuyuk + 1 }], engel: null };
}

/**
 * Görevi aynı gün içinde bir yukarı/aşağı taşır — TAKAS yöntemiyle.
 *
 * Komşu, `sira - 1` DEĞİL listedeki komşudur. Sıra numaraları
 * silinen görevlerden dolayı boşluklu olabiliyor; aritmetik yapsaydık
 * var olmayan bir sıraya yazıp görevi görünmez yerlere atardık.
 *
 * İki görevin sırası EŞİTSE takas hiçbir şeyi değiştirmez (ikisi de
 * aynı numarayı alır ve sıralama rastgeleye kalır). O durumda taşınan
 * göreve komşunun bir ötesindeki numara veriliyor.
 */
export function siraDegistir(
  gorev: TasinabilirGorev,
  yon: "yukari" | "asagi",
  gunGorevleri: readonly TasinabilirGorev[],
): TasimaSonucu {
  const sirali = [...gunGorevleri].sort((a, b) => a.sira - b.sira);
  const i = sirali.findIndex((g) => g.id === gorev.id);
  if (i === -1) return YAPILAMAZ("Görev bu günde bulunamadı.");

  const j = yon === "yukari" ? i - 1 : i + 1;
  if (j < 0) return YAPILAMAZ("Görev zaten en üstte.");
  if (j >= sirali.length) return YAPILAMAZ("Görev zaten en altta.");

  const komsu = sirali[j]!;

  if (komsu.sira === gorev.sira) {
    // Eşit numara: takas etkisiz olurdu. Taşınana komşunun ötesinde
    // bir numara veriliyor; komşuya dokunulmuyor.
    const yeni = yon === "yukari" ? komsu.sira - 1 : komsu.sira + 1;
    return { cagrilar: [{ gorevId: gorev.id, gun: gorev.gun, sira: yeni }], engel: null };
  }

  return {
    cagrilar: [
      { gorevId: gorev.id, gun: gorev.gun, sira: komsu.sira },
      { gorevId: komsu.id, gun: komsu.gun, sira: gorev.sira },
    ],
    engel: null,
  };
}
